import {
  Banner,
  Button,
  css,
  TextArea,
  TextField,
  view,
} from '@sail/ui/deprecated';
import {captureException, captureMessage} from '@sentry/browser';
import {Conversation} from '@stripe-internal/conversations-client';
import {useTheme} from '@stripe-internal/conversations-ui';
import {ChangeEvent, useCallback, useContext, useState} from 'react';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';
import {
  Audience,
  ContactFormEndpointRequest,
  ContactFormEndpointResponse,
  ContactFormEndpointResponseClientError,
} from '@stripe-internal/conversations-shared-dependencies';
import {buildUHConversationIssueContext} from '../../../helpers/buildUHIssueContext';
import {EmailConfirmation} from './EmailConfirmation';
import {ConfigContext} from '../../../Providers/ConfigProvider';
import {AttachmentPreviewList} from '../Attachments/AttachmentPreviewList';
import {Attachment} from '../Attachments/Attachment';
import {CloseButton} from '../CloseButton';
import {AttachmentUploader} from '../Attachments';
import {useClientAnalytics} from '../../../Providers/ClientProvider/useClientAnalytics';
import {BrandedPrimaryButton} from '../BrandedPrimaryButton';
import {useProfile} from '../../../Providers/ProfileProvider';
import {useMountEffect} from '../../../useMountEffect';
import {useApi} from '../../../Providers/ApiProvider/ApiProvider';
import {useNewDesignsEnabled} from '../../../Screens/ChatScreen/useNewDesignsEnabled';

export const messages = defineMessages({
  formTitle: {
    id: 'support_conversations_client.supportWidget.emailForm.title',
    description: 'Title shown in the support widget contact form',
    defaultMessage: 'Send us an email',
  },
  formEmailLabel: {
    id: 'support_conversations_client.supportWidget.emailForm.formEmailLabel',
    description: 'Label shown for email input in support widget contact form',
    defaultMessage: 'Your email',
  },
  formEmailValidationMessage: {
    id: 'support_conversations_client.supportWidget.emailForm.formEmailValidationMessage',
    description:
      'Validation message shown for email input in support widget contact form',
    defaultMessage: 'Please enter a valid email address for us to contact you.',
  },
  formIssueDescriptionLabel: {
    id: 'support_conversations_client.supportWidget.emailForm.formIssueDescriptionLabel',
    description: 'Label for issue description in support widget contact form',
    defaultMessage: 'Provide details about what you need help with',
  },
  formIssueDescriptionPlaceholder: {
    id: 'support_conversations_client.supportWidget.emailForm.formIssueDescriptionPlaceholder',
    description:
      'Placeholder for issue description in support widget contact form',
    defaultMessage: 'Give us as much information as you can',
  },
  formSubmitButtonText: {
    id: 'support_conversations_client.supportWidget.emailForm.formSubmitButtonText',
    description: 'Submit button text in support widget contact form',
    defaultMessage: 'Send',
  },
  cancelButtonText: {
    id: 'support_conversations_client.supportWidget.emailForm.cancelButtonText',
    description:
      'Text shown for the cancel button when on the email form screen',
    defaultMessage: 'Cancel',
  },
  addAttachmentButtonText: {
    id: 'support_conversations_client.supportWidget.emailForm.addAttachmentButtonText',
    description:
      'Text shown for the add attachment button when on the email form screen',
    defaultMessage: 'Add attachment',
  },
  formSubmissionError: {
    id: 'support_conversations_client.supportWidget.emailForm.formSubmissionError',
    description:
      'Text shown when there was an error submitting the contact form',
    defaultMessage:
      'An unexpected error occurred when submitting your support request. We have saved your input.',
  },
  rateLimitedError: {
    id: 'support_conversations_client.supportWidget.emailForm.rateLimitedError',
    description: 'Text shown when the user has submitted too many requests',
    defaultMessage:
      'You have submitted too many requests. Please try again later.',
  },
  emailSupportLabel: {
    id: 'support_conversations_client.supportWidget.emailForm.emailSupportLabel',
    description:
      'Label for the email support option in the support widget contact form',
    defaultMessage: 'Contact Support',
  },
  ccEmailsPlaceholder: {
    id: 'support_conversations_client.supportWidget.ccEmails.placeholder',
    description: 'Placeholder for optional input CC email addresses',
    defaultMessage: 'jdoe@example.com, billing@example.com',
  },
  ccEmailsLabel: {
    id: 'support_conversations_client.supportWidget.ccEmails.label',
    description: 'Label for optional input CC email addresses',
    defaultMessage: 'Add people to the conversation (optional)',
  },
  ccEmailsError: {
    id: 'support_conversations_client.supportWidget.ccEmails.error',
    description: 'Error message for optional input CC email addresses',
    defaultMessage: 'Please enter valid email addresses.',
  },
  unauthenticatedEmailPlaceholder: {
    id: 'support_conversations_client.supportWidget.unauthenticatedEmail.placeholder',
    description:
      'Placeholder for optional email address input when not logged in',
    defaultMessage: 'you@example.com',
  },
  unauthenticatedEmailLabel: {
    id: 'support_conversations_client.supportWidget.unauthenticatedEmail.label',
    description: 'Label for optional email address input when not logged in',
    defaultMessage: 'Your email',
  },
  unauthenticatedEmailError: {
    id: 'support_conversations_client.supportWidget.unauthenticatedEmail.error',
    description:
      'Error message for optional email address input when not logged in',
    defaultMessage: 'Please enter a valid email address for us to contact you.',
  },
  messageSentMessage: {
    id: 'support_conversations_client.emailConfirmation.messageSentMessage',
    description: 'Message added to the chat log after email sent',
    defaultMessage:
      "Thanks, we've received your message, and will try to get back to you by email within 48 hours.",
  },
});

const MAX_ATTACHMENT_COUNT = 5;

const buildEmailContactUrl = (
  formRequest: ContactFormEndpointRequest,
  supportSiteUrl: string,
  token: string,
) => {
  const {email, topic, body} = formRequest;

  // If supportSiteUrl is unset, the empty value will cause the JS `new URL()`
  // constructor to throw a TypeError, since it requires an absolute URL.
  // This is passed via configcontext and we can't necessarily guarantee its non-emptiness.
  // Since this URL is already used in a fallback path (where email sending in UH errors),
  // fall back explicitly on support.stripe.com to avoid an exception.
  const baseUrl = supportSiteUrl || `https://support.stripe.com`;
  const url = new URL(`${baseUrl}/contact/email`);

  // Encrypted token to allow /contact/email to render without redirecting
  url.searchParams.append('token', token);

  url.searchParams.append('email', email);
  if (!topic || topic === 'other') {
    // With no topic or the 'other' topic, give the user a chance to explicitly select a question/topic in the form.
    // Avoid falling back to the "subject" as the initialQuestion value by sending an empty question param.
    url.searchParams.append('question', '');
  } else {
    // For non-'other' topics, pre-fill the topic and set question as "other" so the topic selector appears (on Stripe Support).
    url.searchParams.append('question', 'other');
    url.searchParams.append('topic', topic);
  }
  url.searchParams.append('body', body);
  return url.href;
};

class SubmitError extends Error {
  constructor(
    message: string,
    public type?: ContactFormEndpointResponseClientError['type'],
  ) {
    super(message);
    this.name = 'SubmitError';
  }
}

const throwSubmitErrorIfPresent = (response: ContactFormEndpointResponse) => {
  if (response.type === 'error' && response.error) {
    throw new SubmitError(`Error submitting contact form: ${response.error}`);
  }

  if (response.type === 'invalid-email') {
    throw new SubmitError('Invalid email field in contact form', response.type);
  }

  if (response.type === 'invalid-cc-emails') {
    throw new SubmitError(
      'Invalid cc emails field in contact form',
      response.type,
    );
  }

  if (response.type === 'invalid-message') {
    throw new SubmitError(
      'Invalid message field in contact form',
      response.type,
    );
  }

  if (response.type === 'invalid-topic') {
    throw new SubmitError('Invalid topic field in contact form', response.type);
  }

  if (response.type === 'rate-limited') {
    throw new SubmitError('Rate limit exceeded', response.type);
  }

  if (response.type !== 'success') {
    throw new SubmitError(
      'Unspecified error encountered during form submission.',
    );
  }
};

function SubmissionError({emailUrl}: {emailUrl: string}) {
  const intl = useIntl();
  const cta = (
    <Button href={emailUrl} target={emailUrl}>
      <FormattedMessage {...messages.emailSupportLabel} />
    </Button>
  );

  return (
    <view.div css={{paddingBottom: css.value('12px')}}>
      <Banner
        type="critical"
        description={intl.formatMessage(messages.formSubmissionError)}
        actions={cta}
      />
    </view.div>
  );
}

const RateLimitedError = () => {
  const intl = useIntl();

  return (
    <view.div css={{paddingBottom: css.value('12px')}}>
      <Banner
        type="critical"
        description={intl.formatMessage(messages.rateLimitedError)}
      />
    </view.div>
  );
};

type Props = {
  conversation: Conversation;
  onCancel: () => void;
};

export function EmailForm({conversation, onCancel}: Props) {
  const intl = useIntl();
  const theme = useTheme();
  const {submitContactForm} = useApi();
  const analytics = useClientAnalytics();
  const useNewDesigns = useNewDesignsEnabled();
  const [question, setQuestion] = useState<string | undefined>(
    (conversation.context.emailIssueQuestion as string | undefined) ||
      (conversation.context.latestQuestion as string | undefined),
  );
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [submissionPending, setSubmissionPending] = useState(false);
  const [fallbackEmailUrl, setFallbackEmailUrl] = useState<
    string | undefined
  >();
  const [rateLimitedError, setRateLimitedError] = useState<boolean>(false);

  const [isAddingAttachment, setIsAddingAttachment] = useState<boolean>(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);

  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [ccEmailsError, setCcEmailsError] = useState<boolean>(false);

  const [unauthenticatedEmail, setUnauthenticatedEmail] = useState<
    string | undefined
  >();
  const [unauthenticatedEmailError, setUnauthenticatedEmailError] =
    useState<boolean>(false);

  // Tracks whether there has _ever_ been an unauthenticated email error,
  // during the lifetime of the component.
  const [
    everHadUnauthenticatedEmailError,
    setEverHadUnauthenticatedEmailError,
  ] = useState<boolean>(false);

  const {audience, token, urls} = useContext(ConfigContext);
  const {profile, reloadProfile} = useProfile();
  const {visitor, merchant, supportPlan} = profile;

  useMountEffect(() => {
    // reload user's profile to ensure we have an update to date API session key
    reloadProfile();
  });

  const handleSubmit = useCallback(async () => {
    setSubmissionPending(true);
    analytics.action('support_conversations_email_submit', {
      attachmentCount: attachments.length,
      loggedInMerchant: !!(visitor.sessionApiKey && merchant?.id),
    });

    const {entryPoint: entrypoint, topic} = conversation.context;
    const analyticsEventId = await analytics.requestAnalyticsEventId();

    // This should never happen, let's report if it does.
    if (audience !== conversation.context.audience) {
      captureMessage(
        `Conversation audience does not match page config audience`,
        'error',
      );
    }

    const defaultTopic = audience === 'express' ? 'connect' : 'other';

    const request: ContactFormEndpointRequest = {
      email: unauthenticatedEmail || '',
      body: question || '',
      topic: (topic as ContactFormEndpointRequest['topic']) || defaultTopic,
      audience: audience as Audience,
      entrypoint: entrypoint as ContactFormEndpointRequest['entrypoint'],
      universalHelpConversationId: conversation.id,
      issueContext: buildUHConversationIssueContext(conversation),
      analyticsEventId: analyticsEventId || undefined,
      attachmentIds: attachments.map((attachment) => attachment.id),
      ccEmails,
    };

    try {
      const resp = await submitContactForm(request);
      throwSubmitErrorIfPresent(resp);

      setSubmissionSuccess(true);
      analytics.track('support_conversations_email_success');

      // In drawer, show success message in chat log and end the conversation
      if (useNewDesigns) {
        conversation.setContextValue('channel', 'email');
        conversation.log.appendMetaEvent({
          message: messages.messageSentMessage,
          style: 'body',
        });
        conversation.leave();
      }
    } catch (e: any) {
      if (e instanceof SubmitError && e.type === 'rate-limited') {
        setRateLimitedError(true);
        analytics.track('support_conversations_email_rate_limited');
      } else if (e instanceof SubmitError && e.type === 'invalid-cc-emails') {
        setCcEmailsError(true);
      } else if (e instanceof SubmitError && e.type === 'invalid-email') {
        setUnauthenticatedEmailError(true);
        setEverHadUnauthenticatedEmailError(true);
      } else {
        captureException(e);
        setFallbackEmailUrl(
          buildEmailContactUrl(request, urls.supportSite, token),
        );
        analytics.track('support_conversations_email_error');
      }
    }

    setSubmissionPending(false);
  }, [
    analytics,
    attachments,
    visitor,
    merchant,
    conversation,
    audience,
    unauthenticatedEmail,
    question,
    ccEmails,
    submitContactForm,
    useNewDesigns,
    urls.supportSite,
    token,
  ]);

  const handleShowAddAttachment = useCallback(() => {
    analytics.action('support_conversations_email_clicked_add_attachment');
    setIsAddingAttachment(true);
  }, [analytics]);

  const handleAttachmentsAdded = useCallback((newAttachments: Attachment[]) => {
    setAttachments((currentAttachments) => [
      ...currentAttachments,
      ...newAttachments,
    ]);
  }, []);

  const handleAttachmentRemoved = useCallback((attachment: Attachment) => {
    setAttachments((currentAttachments) =>
      currentAttachments.filter(
        (currentAttachment) => currentAttachment.id !== attachment.id,
      ),
    );
  }, []);

  const onChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value || '';
      setQuestion(value);
      conversation.setContextValue('emailIssueQuestion', value);
    },
    [conversation],
  );

  // Attachments are only supported when there is a logged in merchant
  const allowAttachments = !!(visitor.sessionApiKey && merchant?.id);

  if (submissionSuccess) {
    return <EmailConfirmation conversation={conversation} />;
  }

  if (isAddingAttachment) {
    return (
      <AttachmentUploader
        attachments={attachments}
        remainingAttachmentCount={MAX_ATTACHMENT_COUNT - attachments.length}
        onAttachmentsAdded={handleAttachmentsAdded}
        onRemoveAttachment={handleAttachmentRemoved}
        onDismiss={() => setIsAddingAttachment(false)}
      />
    );
  }

  // For now, the Stripe audience is the only one that doesn't require email confirmation and
  // we plan to change this, see MSE-3179. Once that ticket is done, we can remove this.
  const requireConfirmedEmails = audience !== 'stripe';
  const showUnauthenticatedEmailField =
    !merchant || (!visitor.emailConfirmed && requireConfirmedEmails);
  const showCcEmailsField = !!supportPlan;

  return (
    <view.div
      data-testid="support-widget-email-form"
      css={{position: css.value('relative')}}
    >
      <view.form onSubmit={(e) => e.preventDefault()}>
        <view.div
          css={{
            font: 'heading',
            fontWeight: 'semibold',
            marginBottom: 'medium',
          }}
        >
          {intl.formatMessage(messages.formTitle)}
        </view.div>

        {rateLimitedError ? <RateLimitedError /> : null}
        {fallbackEmailUrl ? (
          <SubmissionError emailUrl={fallbackEmailUrl} />
        ) : null}

        {/* Even if the props suggest not to show the unauthenticated email field,
           we still render it if there is an error, so that the user can fix it.
           We use everHadUnauthenticatedEmailError because we clear the basic
           unauthenticatedEmailError state once the user starts typing into the field */}
        {(showUnauthenticatedEmailField ||
          everHadUnauthenticatedEmailError) && (
          <view.div css={{marginTop: 'medium', marginBottom: 'medium'}}>
            <TextField
              label={intl.formatMessage(messages.unauthenticatedEmailLabel)}
              placeholder={intl.formatMessage(
                messages.unauthenticatedEmailPlaceholder,
              )}
              value={unauthenticatedEmail}
              onChange={(e) => {
                setUnauthenticatedEmailError(false);
                setUnauthenticatedEmail(e.target.value.trim());
              }}
              error={
                unauthenticatedEmailError &&
                intl.formatMessage(messages.unauthenticatedEmailError)
              }
              data-testid="email-form-unauthed-email-input"
              id="unauthenticatedEmailInput"
            />
          </view.div>
        )}

        <view.div>
          <TextArea
            label={intl.formatMessage(messages.formIssueDescriptionLabel)}
            placeholder={intl.formatMessage(
              messages.formIssueDescriptionPlaceholder,
            )}
            value={question || ''}
            onChange={onChange}
            required
            resizeable
            rows={8}
            id="question"
            css={{
              '& textarea:focus': {
                outline: theme.focusVisibleOutline,
                focusRing: theme.focusVisibleOutline
                  ? css.value('none')
                  : undefined,
              },
            }}
          />
        </view.div>

        {showCcEmailsField && (
          <view.div css={{marginTop: 'medium'}}>
            <TextField
              label={intl.formatMessage(messages.ccEmailsLabel)}
              placeholder={intl.formatMessage(messages.ccEmailsPlaceholder)}
              value={ccEmails}
              onChange={(e) => {
                setCcEmailsError(false);
                setCcEmails(
                  e.target.value.split(',').map((email) => email.trim()),
                );
              }}
              error={
                ccEmailsError && intl.formatMessage(messages.ccEmailsError)
              }
              data-testid="email-form-cc-input"
              id="emailCcInput"
            />
          </view.div>
        )}

        <AttachmentPreviewList
          attachments={attachments}
          onRemoveAttachment={handleAttachmentRemoved}
        />

        <view.div css={{marginTop: 'medium'}}>
          <BrandedPrimaryButton
            onPress={handleSubmit}
            disabled={
              !!fallbackEmailUrl ||
              submissionPending ||
              !question ||
              question.trim().length === 0
            }
            pending={submissionPending}
            dataTestId="support-widget-email-form-submit-button"
            size="medium"
          >
            {intl.formatMessage(messages.formSubmitButtonText)}
          </BrandedPrimaryButton>
        </view.div>
        {allowAttachments ? (
          <Button
            type="secondary"
            size="medium"
            css={{width: 'fill', marginTop: 'small'}}
            onPress={handleShowAddAttachment}
            data-testid="add-attachment-button"
            disabled={attachments.length >= MAX_ATTACHMENT_COUNT}
          >
            <FormattedMessage {...messages.addAttachmentButtonText} />
          </Button>
        ) : null}
        <CloseButton
          onClick={onCancel}
          dataTestId="support-widget-email-form-cancel-button"
        />
      </view.form>
    </view.div>
  );
}
